import { useMemo } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { Button, ListBox, ListBoxItem, Popover, Select, SelectValue } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import { type DimensionType } from '../../../../types';
import { getDimensionDisplayName } from '../../../utils/getDimensionDisplayName';

import styles from './PickerBase.module.css';

type DimensionPickerProps = {
  value?: DimensionType;
  onChange: (key: DimensionType | undefined) => void;
  dimensions: DimensionType[];
};

type DimensionItem = {
  key: DimensionType | 'all';
  name: string;
};

const ALL_DIMENSIONS_ID = 'all';

export function DimensionPicker({ value, onChange, dimensions }: DimensionPickerProps) {
  const dimensionItems = useMemo<DimensionItem[]>(
    () => [
      { key: ALL_DIMENSIONS_ID, name: msg('billing.usageControls.allDimensions') },
      ...dimensions.map((key) => ({ key, name: getDimensionDisplayName(key) })),
    ],
    [dimensions],
  );

  return (
    <Select
      value={value ?? ALL_DIMENSIONS_ID}
      onChange={(key) => {
        if (key === ALL_DIMENSIONS_ID) {
          onChange(undefined);
        } else {
          onChange(key as DimensionType);
        }
      }}
      aria-label="dimension-picker"
      placeholder={msg('billing.usageControls.dimensionPlaceholder')}
      className={clsx(styles.container, styles.wide, { [styles.selected]: value })}
    >
      <>
        <Button className={styles.usageButton}>
          <SelectValue className={styles.selectedValue} />
          <Icon name="chevron-down" size="small" slot="end-icon" />
        </Button>
        <Popover>
          <ListBox className={styles.options}>
            {dimensionItems.map(({ key, name }) => (
              <ListBoxItem id={key} key={key} className={styles.optionItem}>
                {name}
              </ListBoxItem>
            ))}
          </ListBox>
        </Popover>
      </>
    </Select>
  );
}
